import { useQueryClient } from '@tanstack/react-query';
import { normalizeMapObject, type MapObject } from '../../../../models/mapObject';
import { createPlotAdminPlotsPost } from '../../../../client';
import useMapModalStore from '../../../../stores/useMapModalStore';
import MapObjectTypeSelectionModal, { type MapObjectType } from '../../../../ui/modals/map/MapObjectTypeSelectionModal';
import { showAlert } from '../../../../utils/telegram/alert';

/**
 * Admin object-type picker (`activeModal === 'typeSelection'`).
 *
 * Close handler and type-select create used to live in MapCanvas and were
 * prop-drilled; both bodies are store + queryClient only, so they live here.
 */
export default function MapObjectTypeSelectionHost() {
  const queryClient = useQueryClient();
  const isMapObjectTypeSelectionModalOpen = useMapModalStore((s) => s.activeModal === 'typeSelection');
  const closeModal = useMapModalStore((s) => s.closeModal);
  const pendingMapObjectCreation = useMapModalStore((s) => s.payload.pendingMapObjectCreation);
  const setPendingMapObjectCreation = useMapModalStore((s) => s.setPendingMapObjectCreation);

  const handleMapObjectTypeSelectionClose = () => {
    closeModal();
    setPendingMapObjectCreation(null);
  };

  const handleMapObjectTypeSelect = (type: MapObjectType) => {
    if (!pendingMapObjectCreation) return;

    closeModal();

    const isIsland = type.startsWith('island_');
    const isParadise = type === 'island_paradise';
    createPlotAdminPlotsPost({
      body: {
        x: pendingMapObjectCreation.x,
        y: pendingMapObjectCreation.y,
        type: type,
        ...(isIsland && { display_width: isParadise ? 1000 : 960, display_height: isParadise ? 1000 : 856 }),
      },
      throwOnError: true,
    })
      .then(({ data }) => normalizeMapObject(data))
      .then((newMapObject) => {
        // Оновлюємо кеш використовуючи повернутий об'єкт (без повного refetch)
        queryClient.setQueryData<MapObject[]>(['mapObjects'], (oldData = []) => {
          return [...oldData, newMapObject];
        });
      })
      .catch((error) => {
        console.error('[MapCanvas] Error creating map object:', error);
        queryClient.invalidateQueries({ queryKey: ['mapObjects'] });
        showAlert('Помилка при створенні об\'єкта');
      })
      .finally(() => {
        setPendingMapObjectCreation(null);
      });
  };

  return (
        <MapObjectTypeSelectionModal
          isOpen={isMapObjectTypeSelectionModalOpen}
          onSelectType={handleMapObjectTypeSelect}
          onClose={handleMapObjectTypeSelectionClose}
        />
  );
}
